/**
 * messageDeepLink — KAP-309 Copy-link helper.
 *
 * The app uses BrowserRouter (App.js), so the canonical conversation URL
 * is `/conversations/{id}` with no hash. We add `?msg={messageId}` as a
 * shallow query param; UnifiedChatPanel reads it on mount, scrolls the
 * matching MessageBubble into view, and clears the param via
 * `history.replaceState` so back/forward doesn't re-trigger.
 *
 * Kept dependency-free (no React, no router) so it can be unit-tested
 * in isolation and called from anywhere a deep link is needed
 * (kebab "Copy link", Slack/Email export, future "share message" flows).
 */

export const MSG_PARAM = 'msg';

export function buildMessageDeepLink({ origin, conversationId, messageId }) {
  if (!conversationId || !messageId) return null;
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!base) return null;
  return `${base}/conversations/${encodeURIComponent(conversationId)}?${MSG_PARAM}=${encodeURIComponent(messageId)}`;
}

export async function copyMessageDeepLink({ conversationId, messageId, clipboard, origin }) {
  const link = buildMessageDeepLink({
    origin: origin || (typeof window !== 'undefined' ? window.location.origin : ''),
    conversationId,
    messageId
  });
  if (!link) return { ok: false, link: null, error: new Error('Missing conversation or message id.') };
  const target = clipboard || (typeof navigator !== 'undefined' ? navigator.clipboard : null);
  if (!target || typeof target.writeText !== 'function') {
    return { ok: false, link, error: new Error('Clipboard API unavailable.') };
  }
  try {
    await target.writeText(link);
    return { ok: true, link, error: null };
  } catch (err) {
    return { ok: false, link, error: err };
  }
}

export function parseMessageDeepLink(search) {
  if (!search) return null;
  const params = new URLSearchParams(search);
  const value = params.get(MSG_PARAM);
  return value ? value : null;
}
